// Users & permissions → Identity providers (approved role UI tab "Identity providers": single sign-on
// & provisioning — Microsoft Entra ID, SAML / OIDC, SCIM, LDAP / AD) and Role preview (PB-17-01).
// ADR-011. The API authorises every action; buttons only reflect the session's capabilities.
import type {
  AccessCatalogueResponse,
  DirectoryConnectionSummary,
  DirectoryRunResponse,
  DirectoryTestResponse,
  IdentityOverviewResponse,
  IdentityProviderDetail,
  IdentityProviderSummary,
  MappingRuleResponse,
  ScimTokenIssued,
  SsoStartResponse,
  SsoTestResult,
  UserIdentitiesResponse,
} from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { ApiRequestError, api, get, newIdempotencyKey } from '../api.ts';
import { LDAP_FORM_DEFAULTS, LdapConfigFields, LdapEditDialog, LdapFacts, LdapPasswordDialog, LdapTestResult, ldapConfigOf, type LdapForm } from '../components/LdapDirectory.tsx'; // PD-590 – PD-597
import { useSiteName } from '../components/Shell.tsx';
import { Dialog, ErrorState, ErrorSummary, Field, Loading, PageHead, StatusBadge } from '../components/ui.tsx';
import { useI18n } from '../i18n.tsx';
import { useSession } from '../session.tsx';
import { UsersTabs } from './People.tsx';

const ROLES = ['org-admin', 'site-admin', 'host', 'reception', 'approver', 'guard', 'permit', 'emergency', 'analyst', 'auditor', 'privacy', 'operator'];
const TENANT_WIDE = new Set(['org-admin', 'analyst', 'auditor', 'privacy']);
const SPECIALISTS = ['identity-verifier', 'credential-issuer', 'privacy-authority', 'report-exporter', 'sensitive-auditor', 'muster-exporter', 'approval-coordinator', 'delegated-host', 'company-suspension'];
const ELEVATED = new Set(['org-admin', 'site-admin']);

const fieldErrors = (e: unknown) => (e instanceof ApiRequestError ? e.fields : {});

function Copyable({ value }: { value: string }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  return (
    <span className="row" style={{ alignItems: 'flex-start' }}>
      <code data-ltr style={{ overflowWrap: 'anywhere', minInlineSize: 0, flex: 1 }}>{value}</code>
      <button
        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        className="button small quiet"
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => setDone(true));
        }}
      >
        {done ? t('common.copied') : t('common.copy')}
      </button>
    </span>
  );
}

/** Reason-confirmed command dialog (every administration change carries a reason, PB-17-12). */
function ConfirmDialog({ title, subtitle, confirm, run, onClose, onDone, danger, children }: { title: string; subtitle: string; confirm: string; run: (reason: string) => Promise<unknown>; onClose: () => void; onDone: () => void; danger?: boolean; children?: ReactNode }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const m = useMutation({ mutationFn: () => run(reason), onSuccess: () => { onDone(); onClose(); } });
  return (
    <Dialog open title={title} subtitle={subtitle} onClose={onClose}>
      <form noValidate onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <ErrorSummary error={m.error} fieldIds={{ reason: 'confirm-reason' }} />
        {children}
        <Field id="confirm-reason" label={t('common.reason')} hint={t('common.reasonHint')} error={fieldErrors(m.error).reason} full>
          {(p) => <textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />}
        </Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
          <button className={`button ${danger ? 'danger' : 'primary'}`} type="submit" disabled={m.isPending}>{m.isPending ? t('common.applying') : confirm}</button>
        </div>
      </form>
    </Dialog>
  );
}

function useOverview() {
  const { scopeKey } = useSession();
  return useQuery({ queryKey: [...scopeKey, 'identity', 'overview'], queryFn: () => get<IdentityOverviewResponse>('/api/v1/identity/overview') });
}

export function IdentityPage() {
  const { t } = useI18n();
  const q = useOverview();
  return (
    <>
      <PageHead eyebrow={t('users.eyebrow')} title={t('users.title')} intro={t('identity.sub')} />
      <UsersTabs />
      <div className="scope-note">
        <strong>{t('identity.panel')}</strong>
        <p>{t('identity.panelSub')}</p>
        <p>{t('identity.simulatorNote')}</p>
      </div>
      {q.isPending ? <Loading label={t('identity.loading')} /> : q.isError ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : (
        <>
          <ProvidersPanel o={q.data} />
          <ScimPanel o={q.data} />
          <DirectoryPanel o={q.data} />
        </>
      )}
    </>
  );
}

// ------------------------------------------------------------------ providers
function ProvidersPanel({ o }: { o: IdentityOverviewResponse }) {
  const { t } = useI18n();
  const { can } = useSession();
  const [adding, setAdding] = useState(false);
  return (
    <section className="panel" aria-labelledby="idp-title">
      <div className="panel-head">
        <div>
          <h2 id="idp-title">{t('identity.providers')}</h2>
          <p>{t('identity.providersSub')}</p>
        </div>
        {can('identity.provider.manage') ? <button className="button primary" type="button" onClick={() => setAdding(true)}>{t('identity.providers.add')}</button> : null}
      </div>
      {!o.entitlements.sso ? <div className="pad"><p className="note">{t('identity.entitlementMissing')}</p></div> : null}
      {o.providers.length === 0 ? (
        <div className="empty"><p>{t('identity.providers.none')}</p></div>
      ) : (
        <div className="tablewrap" tabIndex={0} aria-label={t('identity.providers')} data-scroll-hint={t('common.scrollHint')}>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('identity.col.provider')}</th>
                <th scope="col">{t('identity.col.protocol')}</th>
                <th scope="col">{t('identity.col.status')}</th>
                <th scope="col">{t('identity.col.tested')}</th>
                <th scope="col">{t('identity.col.linked')}</th>
                <th scope="col">{t('identity.col.action')}</th>
              </tr>
            </thead>
            <tbody>
              {o.providers.map((p) => <ProviderRow key={p.id} p={p} />)}
            </tbody>
          </table>
        </div>
      )}
      {adding ? <AddProviderDialog onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function ProviderRow({ p }: { p: IdentityProviderSummary }) {
  const { t, number } = useI18n();
  return (
    <tr>
      <td><strong>{p.displayName}</strong><small>{t(p.preset === 'entra' ? 'identity.preset.entra' : 'identity.preset.generic')}</small></td>
      <td>{t(p.protocol === 'oidc' ? 'identity.protocol.oidc' : 'identity.protocol.saml')}</td>
      <td><StatusBadge status={p.status} /></td>
      <td>{p.testedCurrentVersion ? <span className="badge green">{t('identity.tested.yes')}</span> : <span className="badge amber">{t('identity.tested.no')}</span>}</td>
      <td>{number(p.linkedUsers)}</td>
      <td><Link className="button small" to={`/users/identity/providers/${p.id}`} aria-label={t('identity.openLabel', { name: p.displayName })}>{t('identity.open')}</Link></td>
    </tr>
  );
}

type Kind = 'entra-oidc' | 'oidc' | 'saml' | 'entra-saml';

function AddProviderDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const [kind, setKind] = useState<Kind>('entra-oidc');
  const [f, setF] = useState<Record<string, string>>({ displayName: '', tenantIds: '', issuer: '', authorizationEndpoint: '', tokenEndpoint: '', jwksUri: '', clientId: '', clientSecret: '', groupsClaim: 'groups', idpEntityId: '', ssoUrl: '', certificate: '', reason: '' });
  const [linkByEmail, setLinkByEmail] = useState(true);
  const [trustMfa, setTrustMfa] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const oidc = kind === 'entra-oidc' || kind === 'oidc';
  const entra = kind === 'entra-oidc' || kind === 'entra-saml';
  const tenantIds = f.tenantIds!.split(',').map((s) => s.trim()).filter(Boolean);
  const common = { linkByEmailWithLocalProof: linkByEmail, trustIdpMfa: trustMfa, ...(entra ? { entraTenantIds: tenantIds } : {}) };
  const config = oidc
    ? entra
      ? { clientId: f.clientId, ...common }
      : { issuer: f.issuer, authorizationEndpoint: f.authorizationEndpoint, tokenEndpoint: f.tokenEndpoint, jwksUri: f.jwksUri, clientId: f.clientId, groupsClaim: f.groupsClaim || null, ...common }
    : { idpEntityId: f.idpEntityId, ssoUrl: f.ssoUrl, certificates: f.certificate ? [f.certificate] : [], ...common };
  const m = useMutation({
    mutationFn: () => api<IdentityProviderDetail>('POST', '/api/v1/identity/providers', { protocol: oidc ? 'oidc' : 'saml', preset: entra ? 'entra' : 'generic', displayName: f.displayName, config, ...(oidc && f.clientSecret ? { clientSecret: f.clientSecret } : {}), reason: f.reason }, { idempotencyKey: key }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'identity'] });
      onClose();
    },
  });
  const fe = fieldErrors(m.error);
  const input = (k: string, label: string, opts: { hint?: string; ltr?: boolean; type?: string; err?: string } = {}) => (
    <Field id={`idp-${k}`} label={label} hint={opts.hint} error={fe[opts.err ?? `config.${k}`] ?? fe[k]}>
      {(p) => <input {...p} type={opts.type ?? 'text'} dir={opts.ltr ? 'ltr' : undefined} value={f[k]} onChange={set(k)} autoComplete="off" />}
    </Field>
  );
  return (
    <Dialog open title={t('identity.add.title')} subtitle={t('identity.add.sub')} onClose={onClose}>
      <form noValidate onSubmit={(e: FormEvent) => { e.preventDefault(); m.mutate(); }}>
        <ErrorSummary error={m.error} fieldIds={{}} />
        <div className="formgrid">
          <Field label={t('identity.add.kind')} full>
            {(p) => (
              <select {...p} value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                {(['entra-oidc', 'oidc', 'saml', 'entra-saml'] as const).map((k) => <option key={k} value={k}>{t(`identity.kind.${k}`)}</option>)}
              </select>
            )}
          </Field>
          {input('displayName', t('identity.f.displayName'), { err: 'displayName' })}
          {entra ? input('tenantIds', t('identity.f.tenantIds'), { hint: t('identity.f.tenantIdsHint'), ltr: true, err: 'config.entraTenantIds' }) : null}
          {oidc && !entra ? (
            <>
              {input('issuer', t('identity.f.issuer'), { ltr: true })}
              {input('authorizationEndpoint', t('identity.f.authorizationEndpoint'), { ltr: true })}
              {input('tokenEndpoint', t('identity.f.tokenEndpoint'), { ltr: true })}
              {input('jwksUri', t('identity.f.jwksUri'), { ltr: true })}
              {input('groupsClaim', t('identity.f.groupsClaim'), { ltr: true })}
            </>
          ) : null}
          {oidc ? (
            <>
              {input('clientId', t('identity.f.clientId'), { ltr: true })}
              {input('clientSecret', t('identity.f.clientSecret'), { hint: t('identity.f.clientSecretHint'), type: 'password', err: 'clientSecret' })}
            </>
          ) : (
            <>
              {input('idpEntityId', t('identity.f.idpEntityId'), { ltr: true })}
              {input('ssoUrl', t('identity.f.ssoUrl'), { ltr: true })}
              <Field id="idp-certificate" label={t('identity.f.certificate')} hint={t('identity.f.certificateHint')} error={fe['config.certificates'] ?? fe['config.certificates.0']} full>
                {(p) => <textarea {...p} dir="ltr" rows={5} value={f.certificate} onChange={set('certificate')} />}
              </Field>
            </>
          )}
        </div>
        <div className="checks">
          <label><input type="checkbox" checked={linkByEmail} onChange={(e) => setLinkByEmail(e.target.checked)} /> {t('identity.f.linkByEmail')}</label>
          <label><input type="checkbox" checked={trustMfa} onChange={(e) => setTrustMfa(e.target.checked)} /> {t('identity.f.trustIdpMfa')}</label>
        </div>
        <Field id="idp-reason" label={t('common.reason')} hint={t('common.reasonHint')} error={fe.reason} full>
          {(p) => <textarea {...p} value={f.reason} onChange={set('reason')} maxLength={500} />}
        </Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
          <button className="button primary" type="submit" disabled={m.isPending}>{m.isPending ? t('common.saving') : t('identity.create')}</button>
        </div>
      </form>
    </Dialog>
  );
}

export function ProviderPage() {
  const { providerId = '' } = useParams();
  const { t, dateTime } = useI18n();
  const { scopeKey, can } = useSession();
  const qc = useQueryClient();
  const key = [...scopeKey, 'identity', 'provider', providerId];
  const q = useQuery({ queryKey: key, queryFn: () => get<IdentityProviderDetail>(`/api/v1/identity/providers/${providerId}`) });
  const [dialog, setDialog] = useState<null | 'activate' | 'disable' | 'rotate' | 'rule' | 'cert'>(null);
  const [secret, setSecret] = useState('');
  const [cert, setCert] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: [...scopeKey, 'identity'] });
  };
  const test = useMutation({
    mutationFn: () => api<SsoStartResponse>('POST', `/api/v1/identity/providers/${providerId}/test`, {}),
    onSuccess: (r) => window.location.assign(r.redirectUrl),
  });
  if (q.isPending) return <Loading label={t('identity.loading')} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  const p = q.data;
  const manage = can('identity.provider.manage');
  const certificates = Array.isArray(p.config.certificates) ? (p.config.certificates as string[]) : [];
  const patchCertificates = (list: string[], reason: string) => api('PATCH', `/api/v1/identity/providers/${p.id}`, { expectedVersion: p.version, config: { ...p.config, certificates: list }, reason });
  return (
    <>
      <PageHead eyebrow={t('identity.detail.eyebrow')} title={p.displayName} intro={<Link to="/users/identity">{t('identity.detail.back')}</Link>} actions={<StatusBadge status={p.status} />} />
      <ErrorSummary error={test.error} fieldIds={{}} />
      <div className="cols">
        <section className="panel" aria-labelledby="facts-title">
          <div className="panel-head"><h2 id="facts-title">{t('identity.detail.facts')}</h2></div>
          <div className="pad">
            <dl className="definition">
              <div><dt>{t('identity.col.protocol')}</dt><dd>{t(p.protocol === 'oidc' ? 'identity.protocol.oidc' : 'identity.protocol.saml')} · {t(p.preset === 'entra' ? 'identity.preset.entra' : 'identity.preset.generic')}</dd></div>
              <div><dt>{t('identity.detail.version')}</dt><dd>{p.version}</dd></div>
              <div><dt>{t('identity.col.tested')}</dt><dd>{p.testedCurrentVersion ? t('identity.tested.yes') : t('identity.tested.no')}</dd></div>
              <div><dt>{t('identity.detail.lastTest')}</dt><dd>{p.lastTestedAt ? <>{dateTime(p.lastTestedAt)} · <StatusBadge status={p.lastTestOutcome ?? 'failed'} /></> : t('identity.detail.never')}</dd></div>
              {p.protocol === 'oidc' ? <div><dt>{t('identity.detail.secret')}</dt><dd>{p.secret.configured ? t('identity.detail.secretSet', { version: p.secret.version }) : t('identity.detail.secretNone')}</dd></div> : null}
              <div><dt>{t('identity.col.linked')}</dt><dd>{p.linkedUsers}</dd></div>
            </dl>
            {manage ? (
              <div className="actions" style={{ marginBlockStart: 16 }}>
                {p.status !== 'disabled' ? <button className="button" type="button" onClick={() => test.mutate()} disabled={test.isPending}>{t('identity.detail.test')}</button> : null}
                {p.status !== 'active' && p.testedCurrentVersion ? <button className="button primary" type="button" onClick={() => setDialog('activate')}>{t('identity.detail.activate')}</button> : null}
                {p.protocol === 'oidc' ? <button className="button" type="button" onClick={() => setDialog('rotate')}>{t('identity.detail.rotate')}</button> : null}
              </div>
            ) : null}
            {can('identity.disable') && p.status !== 'disabled' ? <div className="actions" style={{ marginBlockStart: 8 }}><button className="button danger" type="button" onClick={() => setDialog('disable')}>{t('identity.detail.disable')}</button></div> : null}
            <p className="tiny">{t('identity.detail.testHint')}</p>
          </div>
        </section>
        <section className="panel" aria-labelledby="reg-title">
          <div className="panel-head"><div><h2 id="reg-title">{t('identity.detail.register')}</h2><p>{t('identity.detail.registerSub')}</p></div></div>
          <div className="pad">
            <dl className="definition" style={{ gridTemplateColumns: '1fr' }}>
              {p.protocol === 'saml' ? (
                <>
                  <div><dt>{t('identity.detail.entityId')}</dt><dd><Copyable value={p.serviceProvider.entityId} /></dd></div>
                  <div><dt>{t('identity.detail.acsUrl')}</dt><dd><Copyable value={p.serviceProvider.acsUrl} /></dd></div>
                  <div><dt>{t('identity.detail.metadataUrl')}</dt><dd><Copyable value={p.serviceProvider.metadataUrl} /></dd></div>
                </>
              ) : (
                <div><dt>{t('identity.detail.redirectUri')}</dt><dd><Copyable value={p.serviceProvider.redirectUri} /></dd></div>
              )}
            </dl>
          </div>
        </section>
      </div>
      {p.protocol === 'saml' ? (
        <section className="panel" aria-labelledby="cert-title">
          <div className="panel-head">
            <div><h2 id="cert-title">{t('identity.detail.certificates')}</h2><p>{t('identity.detail.certificatesSub')}</p></div>
            {manage ? <button className="button" type="button" onClick={() => setDialog('cert')}>{t('identity.detail.addCertificate')}</button> : null}
          </div>
          <div className="tablewrap" tabIndex={0} aria-label={t('identity.detail.certificates')} data-scroll-hint={t('common.scrollHint')}>
            <table>
              <thead><tr><th scope="col">{t('identity.detail.fingerprint')}</th><th scope="col">{t('identity.detail.validTo')}</th><th scope="col">{t('identity.col.action')}</th></tr></thead>
              <tbody>
                {p.certificates.map((c, i) => (
                  <tr key={c.fingerprintSha256}>
                    <td><code data-ltr>{c.fingerprintSha256}</code><small data-ltr>{c.subject ?? t('identity.detail.publicKey')}</small></td>
                    <td>{c.validTo ? dateTime(c.validTo) : '—'}</td>
                    <td>
                      {manage && certificates.length > 1 ? (
                        <RemoveCertificate label={t('identity.detail.removeCertificateLabel', { fingerprint: c.fingerprintSha256.slice(0, 11) })} run={(reason) => patchCertificates(certificates.filter((_, j) => j !== i), reason)} onDone={refresh} />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      <MappingPanel rules={p.mappingRules} providerId={p.id} onChanged={refresh} />
      {dialog === 'activate' ? <ConfirmDialog title={t('identity.detail.activate')} subtitle={t('identity.detail.activateSub')} confirm={t('identity.detail.activate')} run={(reason) => api('POST', `/api/v1/identity/providers/${p.id}/activate`, { expectedVersion: p.version, reason })} onClose={() => setDialog(null)} onDone={refresh} /> : null}
      {dialog === 'disable' ? <ConfirmDialog danger title={t('identity.detail.disable')} subtitle={t('identity.detail.disableSub')} confirm={t('identity.detail.disable')} run={(reason) => api('POST', `/api/v1/identity/providers/${p.id}/disable`, { expectedVersion: p.version, reason })} onClose={() => setDialog(null)} onDone={refresh} /> : null}
      {dialog === 'rotate' ? (
        <ConfirmDialog title={t('identity.detail.rotate')} subtitle={t('identity.detail.rotateSub')} confirm={t('identity.detail.rotate')} run={(reason) => api('POST', `/api/v1/identity/providers/${p.id}/secret`, { expectedVersion: p.version, clientSecret: secret, reason }).finally(() => setSecret(''))} onClose={() => setDialog(null)} onDone={refresh}>
          <Field label={t('identity.f.clientSecret')} hint={t('identity.f.clientSecretHint')} full>
            {(fp) => <input {...fp} type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} />}
          </Field>
        </ConfirmDialog>
      ) : null}
      {dialog === 'cert' ? (
        <ConfirmDialog title={t('identity.detail.addCertificate')} subtitle={t('identity.detail.certificatesSub')} confirm={t('identity.detail.addCertificate')} run={(reason) => patchCertificates([...certificates, cert], reason).finally(() => setCert(''))} onClose={() => setDialog(null)} onDone={refresh}>
          <Field label={t('identity.f.certificate')} hint={t('identity.f.certificateHint')} full>
            {(fp) => <textarea {...fp} dir="ltr" rows={6} value={cert} onChange={(e) => setCert(e.target.value)} />}
          </Field>
        </ConfirmDialog>
      ) : null}
    </>
  );
}

function RemoveCertificate({ label, run, onDone }: { label: string; run: (reason: string) => Promise<unknown>; onDone: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="button small danger" type="button" aria-label={label} onClick={() => setOpen(true)}>{t('identity.detail.removeCertificate')}</button>
      {open ? <ConfirmDialog danger title={label} subtitle={t('identity.detail.certificatesSub')} confirm={t('identity.detail.removeCertificate')} run={run} onClose={() => setOpen(false)} onDone={onDone} /> : null}
    </>
  );
}

// ------------------------------------------------------------------ mapping rules (PD-108)
function MappingPanel({ rules, providerId, groups, onChanged }: { rules: readonly MappingRuleResponse[]; providerId?: string; groups?: IdentityOverviewResponse['scim']['groups']; onChanged: () => void }) {
  const { t, tk } = useI18n();
  const { can } = useSession();
  const siteName = useSiteName();
  const [adding, setAdding] = useState(false);
  const [disabling, setDisabling] = useState<MappingRuleResponse | null>(null);
  const scim = !providerId;
  const manage = can(scim ? 'identity.scim.manage' : 'identity.provider.manage');
  const groupName = (id: string | null) => groups?.find((g) => g.id === id)?.displayName ?? '—';
  return (
    <section className="panel" aria-labelledby={`map-${providerId ?? 'scim'}`}>
      <div className="panel-head">
        <div><h2 id={`map-${providerId ?? 'scim'}`}>{t(scim ? 'mapping.scimTitle' : 'mapping.title')}</h2><p>{t(scim ? 'mapping.scimSub' : 'mapping.sub')}</p></div>
        {manage ? <button className="button" type="button" onClick={() => setAdding(true)}>{t('mapping.add')}</button> : null}
      </div>
      {rules.length === 0 ? <div className="empty"><p>{t('mapping.none')}</p></div> : (
        <div className="tablewrap" tabIndex={0} aria-label={t(scim ? 'mapping.scimTitle' : 'mapping.title')} data-scroll-hint={t('common.scrollHint')}>
          <table>
            <thead><tr><th scope="col">{t('mapping.col.when')}</th><th scope="col">{t('mapping.col.grant')}</th><th scope="col">{t('mapping.col.conferred')}</th><th scope="col">{t('identity.col.status')}</th><th scope="col">{t('identity.col.action')}</th></tr></thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <td>{r.source === 'scim_group' ? t('mapping.whenGroup', { group: groupName(r.scimGroupId) }) : t('mapping.when', { claim: r.claim ?? '', value: r.claimValue ?? '' })}</td>
                  <td>
                    {r.kind === 'specialist' ? tk(`specialist.${r.code}`) : r.kind === 'experience' ? tk(`role.${r.code}.title`) : <code data-ltr>{r.code}</code>} · {siteName(r.siteId)}
                    {r.allowElevated ? <> <span className="badge amber">{t('mapping.elevatedBadge')}</span></> : null}
                  </td>
                  <td>{r.grantsConferred}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{r.status === 'active' && can('identity.disable') ? <button className="button small" type="button" onClick={() => setDisabling(r)}>{t('mapping.disable')}</button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding ? <AddRuleDialog providerId={providerId} groups={groups ?? []} onClose={() => setAdding(false)} onDone={onChanged} /> : null}
      {disabling ? <ConfirmDialog danger title={t('mapping.disable')} subtitle={t('mapping.disableSub')} confirm={t('mapping.disable')} run={(reason) => api('POST', `/api/v1/identity/mapping-rules/${disabling.id}/disable`, { expectedVersion: disabling.version, reason })} onClose={() => setDisabling(null)} onDone={onChanged} /> : null}
    </section>
  );
}

function AddRuleDialog({ providerId, groups, onClose, onDone }: { providerId?: string | undefined; groups: IdentityOverviewResponse['scim']['groups']; onClose: () => void; onDone: () => void }) {
  const { t, tk } = useI18n();
  const { session, can } = useSession();
  const [key] = useState(newIdempotencyKey);
  const [claim, setClaim] = useState('groups');
  const [value, setValue] = useState('');
  const [group, setGroup] = useState(groups.find((g) => !g.deleted)?.id ?? '');
  const [kind, setKind] = useState<'experience' | 'specialist'>('experience');
  const [code, setCode] = useState('host');
  const [siteId, setSiteId] = useState<string>(session.sites[0]?.id ?? '');
  const [elevated, setElevated] = useState(false);
  const [reason, setReason] = useState('');
  const isElevated = kind === 'specialist' || ELEVATED.has(code);
  const allowsTenantWide = kind === 'specialist' || TENANT_WIDE.has(code);
  const m = useMutation({
    mutationFn: () =>
      api('POST', '/api/v1/identity/mapping-rules', {
        source: providerId ? 'sso_claim' : 'scim_group',
        providerId: providerId ?? null,
        claim: providerId ? claim : null,
        claimValue: providerId ? value : null,
        scimGroupId: providerId ? null : group,
        kind,
        code,
        siteId: siteId || null,
        allowElevated: elevated,
        reason,
      }, { idempotencyKey: key }),
    onSuccess: () => { onDone(); onClose(); },
  });
  const fe = fieldErrors(m.error);
  return (
    <Dialog open title={t('mapping.add')} subtitle={t(providerId ? 'mapping.sub' : 'mapping.scimSub')} onClose={onClose}>
      <form noValidate onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <ErrorSummary error={m.error} fieldIds={{}} />
        <div className="formgrid">
          {providerId ? (
            <>
              <Field label={t('mapping.f.claim')} error={fe.claim}>{(p) => <input {...p} dir="ltr" value={claim} onChange={(e) => setClaim(e.target.value)} />}</Field>
              <Field label={t('mapping.f.value')} error={fe.claimValue}>{(p) => <input {...p} dir="ltr" value={value} onChange={(e) => setValue(e.target.value)} />}</Field>
            </>
          ) : (
            <Field label={t('mapping.f.group')} error={fe.scimGroupId} full>
              {(p) => <select {...p} value={group} onChange={(e) => setGroup(e.target.value)}>{groups.filter((g) => !g.deleted).map((g) => <option key={g.id} value={g.id}>{g.displayName}</option>)}</select>}
            </Field>
          )}
          <Field label={t('mapping.f.kind')}>
            {(p) => (
              <select {...p} value={kind} onChange={(e) => { const k = e.target.value as 'experience' | 'specialist'; setKind(k); setCode(k === 'experience' ? 'host' : SPECIALISTS[0]!); }}>
                <option value="experience">{t('mapping.kind.experience')}</option>
                {can('grant.sensitive.issue') ? <option value="specialist">{t('mapping.kind.specialist')}</option> : null}
              </select>
            )}
          </Field>
          <Field label={t('mapping.f.code')} error={fe.code}>
            {(p) => <select {...p} value={code} onChange={(e) => setCode(e.target.value)}>{(kind === 'experience' ? ROLES : SPECIALISTS).map((c) => <option key={c} value={c}>{kind === 'experience' ? tk(`role.${c}.title`) : tk(`specialist.${c}`)}</option>)}</select>}
          </Field>
          <Field label={t('mapping.f.site')} error={fe.siteId}>
            {(p) => (
              <select {...p} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                {allowsTenantWide ? <option value="">{t('common.organisationWide')}</option> : null}
                {session.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </Field>
        </div>
        {isElevated ? (
          <div className="checks">
            <label><input type="checkbox" checked={elevated} onChange={(e) => setElevated(e.target.checked)} /> {t('mapping.f.elevated')}</label>
            <small>{t('mapping.f.elevatedHint')}</small>
            {fe.allowElevated ? <small className="field-error">{fe.allowElevated}</small> : null}
          </div>
        ) : null}
        <Field label={t('common.reason')} hint={t('common.reasonHint')} error={fe.reason} full>
          {(p) => <textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />}
        </Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
          <button className="button primary" type="submit" disabled={m.isPending}>{m.isPending ? t('common.saving') : t('mapping.add')}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ SCIM
function ScimPanel({ o }: { o: IdentityOverviewResponse }) {
  const { t, tk, dateTime } = useI18n();
  const { can, scopeKey } = useSession();
  const qc = useQueryClient();
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<ScimTokenIssued | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: [...scopeKey, 'identity'] });
  return (
    <section className="panel" aria-labelledby="scim-title">
      <div className="panel-head">
        <div><h2 id="scim-title">{t('scim.title')}</h2><p>{t('scim.sub')}</p></div>
        {can('identity.scim.manage') ? <button className="button" type="button" onClick={() => setIssuing(true)}>{t('scim.token.issue')}</button> : null}
      </div>
      <div className="pad">
        {!o.entitlements.scim ? <p className="note">{t('identity.entitlementMissing')}</p> : null}
        <dl className="definition">
          <div><dt>{t('scim.baseUrl')}</dt><dd><Copyable value={o.scim.baseUrl} /></dd></div>
          <div><dt>{t('scim.provisioned')}</dt><dd>{o.scim.provisionedUsers}</dd></div>
        </dl>
        <h3 style={{ marginBlockStart: 16 }}>{t('scim.tokens')}</h3>
        {o.scim.tokens.length === 0 ? <p>{t('scim.tokens.none')}</p> : (
          <ul className="stack">
            {o.scim.tokens.map((tok) => (
              <li key={tok.id} className="row">
                <span><strong>{tok.label}</strong> · {t('scim.token.hint')} <code data-ltr>…{tok.hint}</code> · {t('scim.token.lastUsed')}: {tok.lastUsedAt ? dateTime(tok.lastUsedAt) : t('identity.detail.never')}</span>
                {tok.revokedAt ? <StatusBadge status="revoked" /> : can('identity.disable') ? <button className="button small danger" type="button" onClick={() => setRevoking(tok.id)}>{t('scim.token.revoke')}</button> : null}
              </li>
            ))}
          </ul>
        )}
        <h3 style={{ marginBlockStart: 16 }}>{t('scim.groups')}</h3>
        {o.scim.groups.length === 0 ? <p>{t('scim.groups.none')}</p> : (
          <ul className="chips">{o.scim.groups.filter((g) => !g.deleted).map((g) => <li key={g.id} className="chip">{g.displayName} · {tk('scim.groupMembers', { count: g.members })}</li>)}</ul>
        )}
      </div>
      {o.scim.groups.length ? <MappingPanel rules={o.scim.mappingRules} groups={o.scim.groups} onChanged={refresh} /> : null}
      {issuing ? (
        <ConfirmDialog title={t('scim.token.issue')} subtitle={t('scim.token.issueSub')} confirm={t('scim.token.issue')} run={(reason) => api<ScimTokenIssued>('POST', '/api/v1/identity/scim/tokens', { label, expiresInDays: 365, reason }).then((r) => setIssued(r))} onClose={() => setIssuing(false)} onDone={refresh}>
          <Field label={t('scim.token.label')} full>{(p) => <input {...p} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} />}</Field>
        </ConfirmDialog>
      ) : null}
      {issued ? (
        <Dialog open title={t('scim.token.issued')} subtitle={t('scim.token.issuedBody')} onClose={() => setIssued(null)}>
          <p className="secret"><Copyable value={issued.token} /></p>
          <div className="form-actions"><button className="button primary" type="button" onClick={() => setIssued(null)}>{t('common.close')}</button></div>
        </Dialog>
      ) : null}
      {revoking ? <ConfirmDialog danger title={t('scim.token.revoke')} subtitle={t('scim.token.revokeSub')} confirm={t('scim.token.revoke')} run={(reason) => api('POST', `/api/v1/identity/scim/tokens/${revoking}/revoke`, { reason })} onClose={() => setRevoking(null)} onDone={refresh} /> : null}
    </section>
  );
}

// ------------------------------------------------------------------ directory
function DirectoryPanel({ o }: { o: IdentityOverviewResponse }) {
  const { t } = useI18n();
  const { can } = useSession();
  const [adding, setAdding] = useState(false);
  return (
    <section className="panel" aria-labelledby="dir-title">
      <div className="panel-head">
        <div><h2 id="dir-title">{t('directory.title')}</h2><p>{t('directory.sub')}</p></div>
        {can('identity.directory.manage') ? <button className="button" type="button" onClick={() => setAdding(true)}>{t('directory.add')}</button> : null}
      </div>
      <div className="pad stack">
        {!o.entitlements.directory ? <p className="note">{t('identity.entitlementMissing')}</p> : null}
        {o.directories.length === 0 ? <p>{t('directory.none')}</p> : o.directories.map((d) => <DirectoryCard key={d.id} d={d} />)}
      </div>
      {adding ? <AddDirectoryDialog onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function DirectoryCard({ d }: { d: DirectoryConnectionSummary }) {
  const { t, tk, dateTime } = useI18n();
  const { can, scopeKey } = useSession();
  const qc = useQueryClient();
  const [run, setRun] = useState<DirectoryRunResponse | null>(null);
  const [confirming, setConfirming] = useState<null | 'sync' | 'confirm'>(null);
  const [dialog, setDialog] = useState<null | 'edit' | 'password'>(null);
  const done = () => void qc.invalidateQueries({ queryKey: [...scopeKey, 'identity'] });
  // PD-597: test connection changes nothing; the result shows counts only.
  const test = useMutation({ mutationFn: () => api<DirectoryTestResponse>('POST', `/api/v1/identity/directories/${d.id}/test`, {}), onSuccess: done });
  const manage = can('identity.directory.manage');
  return (
    <div className="task-card">
      <div className="row"><h3>{d.displayName}</h3><StatusBadge status={d.status} /></div>
      <p className="tiny">{tk(`directory.kind.${d.kind}`)}{d.kind === 'simulator' && d.bindSecretRef ? <> · <code data-ltr>{d.bindSecretRef}</code></> : null}</p>
      {d.lastOutcome === 'unknown' ? <p className="note warning" role="status">{t('directory.unknown')}</p> : null}
      {d.stale ? <p className="note warning" role="status">{t('directory.stale')}</p> : null}
      {d.kind === 'ldap' ? <LdapFacts d={d} /> : null}
      <dl className="definition">
        <div><dt>{t('directory.lastRun')}</dt><dd>{d.lastRunAt ? <>{dateTime(d.lastRunAt)} · <StatusBadge status={d.lastOutcome ?? 'failed'} /></> : t('identity.detail.never')}{d.lastError ? <> · {tk(`directory.error.${d.lastError}`)}</> : null}</dd></div>
        <div><dt>{t('directory.lastSuccess')}</dt><dd>{d.lastSuccessAt ? dateTime(d.lastSuccessAt) : t('identity.detail.never')}</dd></div>
        <div><dt>{t('directory.owned')}</dt><dd>{d.ownedAccounts}</dd></div>
      </dl>
      {test.data ? <LdapTestResult r={test.data} /> : null}
      {test.error instanceof ApiRequestError ? <ErrorSummary error={test.error} fieldIds={{}} /> : null}
      {run ? (
        <div className="note" role="status">
          {run.outcome === 'unknown' ? <p>{t('directory.unknown')}</p> : <p>{t('directory.run.result', { ...run.counts })}</p>}
          {run.conflicts.length ? <ul>{run.conflicts.slice(0, 20).map((c, i) => <li key={i}>{tk(`directory.conflict.${c.kind}`)}{c.externalId ? <> · <code data-ltr>{c.externalId}</code></> : null}</li>)}</ul> : null}
        </div>
      ) : null}
      {manage ? (
        <div className="actions">
          <button className="button" type="button" onClick={() => test.mutate()} disabled={test.isPending}>{test.isPending ? t('ldap.testing') : t('ldap.test')}</button>
          {d.status === 'active' ? <button className="button" type="button" onClick={() => setConfirming('sync')}>{t('directory.sync')}</button> : null}
          {d.status === 'active' && d.lastOutcome === 'held' ? <button className="button danger" type="button" onClick={() => setConfirming('confirm')}>{t('directory.confirm')}</button> : null}
          {d.kind === 'ldap' ? <button className="button" type="button" onClick={() => setDialog('edit')}>{t('ldap.edit')}</button> : null}
          {d.kind === 'ldap' ? <button className="button" type="button" onClick={() => setDialog('password')}>{t('ldap.setPassword')}</button> : null}
        </div>
      ) : null}
      {confirming ? (
        <ConfirmDialog
          danger={confirming === 'confirm'}
          title={t(confirming === 'confirm' ? 'directory.confirm' : 'directory.sync')}
          subtitle={t(confirming === 'confirm' ? 'directory.confirmSub' : 'directory.syncSub')}
          confirm={t(confirming === 'confirm' ? 'directory.confirm' : 'directory.sync')}
          run={(reason) => api<DirectoryRunResponse>('POST', `/api/v1/identity/directories/${d.id}/sync`, { confirmDeactivations: confirming === 'confirm', reason }).then(setRun)}
          onClose={() => setConfirming(null)}
          onDone={done}
        />
      ) : null}
      {dialog === 'edit' ? <LdapEditDialog d={d} onClose={() => setDialog(null)} onDone={done} /> : null}
      {dialog === 'password' ? <LdapPasswordDialog d={d} onClose={() => setDialog(null)} onDone={done} /> : null}
    </div>
  );
}

function AddDirectoryDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const [key] = useState(newIdempotencyKey);
  const [kind, setKind] = useState<'ldap' | 'simulator'>('ldap');
  const [f, setF] = useState({ displayName: '', bindPassword: '', secretRef: '', entries: '[]', schedule: '', maxDeactivation: '10', reason: '' });
  const [ldap, setLdap] = useState<LdapForm>(LDAP_FORM_DEFAULTS);
  const [jsonError, setJsonError] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const m = useMutation({
    mutationFn: () => {
      let config: Record<string, unknown>;
      if (kind === 'simulator') {
        try {
          config = { entries: JSON.parse(f.entries) as unknown };
          setJsonError(false);
        } catch {
          setJsonError(true);
          return Promise.reject(new Error('json'));
        }
      } else config = ldapConfigOf(ldap);
      // PD-592: the bind password is sent once and sealed by the API; it is never shown again.
      const secret = kind === 'ldap' && f.bindPassword ? { bindPassword: f.bindPassword } : {};
      return api('POST', '/api/v1/identity/directories', { kind, displayName: f.displayName, config, bindSecretRef: f.secretRef || null, ...secret, scheduleMinutes: f.schedule ? Number(f.schedule) : null, maxDeactivationPercent: Number(f.maxDeactivation), reason: f.reason }, { idempotencyKey: key });
    },
    onSuccess: () => { setF((x) => ({ ...x, bindPassword: '' })); void qc.invalidateQueries({ queryKey: [...scopeKey, 'identity'] }); onClose(); },
  });
  const fe = fieldErrors(m.error);
  return (
    <Dialog open title={t('directory.add')} subtitle={t('directory.sub')} onClose={onClose}>
      <form noValidate onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {m.error instanceof ApiRequestError ? <ErrorSummary error={m.error} fieldIds={{}} /> : null}
        <div className="formgrid">
          <Field label={t('identity.add.kind')} error={fe.kind}>{(p) => <select {...p} value={kind} onChange={(e) => setKind(e.target.value as 'ldap' | 'simulator')}><option value="ldap">{t('directory.kind.ldap')}</option><option value="simulator">{t('directory.kind.simulator')}</option></select>}</Field>
          <Field label={t('identity.f.displayName')} error={fe.displayName}>{(p) => <input {...p} value={f.displayName} onChange={set('displayName')} />}</Field>
          {kind === 'ldap' ? (
            <>
              <LdapConfigFields f={ldap} set={(p) => setLdap((x) => ({ ...x, ...p }))} errors={fe} />
              <Field label={t('ldap.f.bindPassword')} hint={t('ldap.f.bindPasswordHint')} error={fe.bindPassword}>{(p) => <input {...p} type="password" autoComplete="new-password" dir="ltr" value={f.bindPassword} onChange={set('bindPassword')} />}</Field>
              <Field label={t('directory.f.secretRef')} hint={t('directory.f.secretRefHint')} error={fe.bindSecretRef}>{(p) => <input {...p} dir="ltr" value={f.secretRef} onChange={set('secretRef')} />}</Field>
            </>
          ) : (
            <Field label={t('directory.f.entries')} error={jsonError ? t('v.json') : fe['config.entries']} full>{(p) => <textarea {...p} dir="ltr" rows={6} value={f.entries} onChange={set('entries')} />}</Field>
          )}
          <Field label={t('directory.f.schedule')} error={fe.scheduleMinutes}>{(p) => <input {...p} type="number" min={15} value={f.schedule} onChange={set('schedule')} />}</Field>
          <Field label={t('directory.f.maxDeactivation')} error={fe.maxDeactivationPercent}>{(p) => <input {...p} type="number" min={0} max={100} value={f.maxDeactivation} onChange={set('maxDeactivation')} />}</Field>
        </div>
        <Field label={t('common.reason')} hint={t('common.reasonHint')} error={fe.reason} full>{(p) => <textarea {...p} value={f.reason} onChange={set('reason')} maxLength={500} />}</Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
          <button className="button primary" type="submit" disabled={m.isPending}>{m.isPending ? t('common.saving') : t('directory.add')}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ test result
export function TestResultPage() {
  const { t, tk } = useI18n();
  const siteName = useSiteName();
  const [result] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('r') ?? '');
  // The one-time result never stays in the address bar or history.
  useEffect(() => {
    if (window.location.hash) history.replaceState(null, '', window.location.pathname);
  }, []);
  const q = useQuery({ queryKey: ['identity-test', result], queryFn: () => api<SsoTestResult>('POST', '/api/v1/identity/providers/test-result', { result }), enabled: result.length > 20, retry: false, staleTime: Infinity });
  if (!result || q.isError) return <><PageHead eyebrow={t('identity.detail.eyebrow')} title={t('test.title')} /><p className="note danger" role="alert">{t('test.invalid')}</p><Link to="/users/identity">{t('identity.detail.back')}</Link></>;
  if (q.isPending) return <Loading label={t('test.loading')} />;
  const r = q.data;
  return (
    <>
      <PageHead eyebrow={t('identity.detail.eyebrow')} title={t('test.title')} actions={<StatusBadge status={r.outcome} />} intro={<Link to={`/users/identity/providers/${r.providerId}`}>{t('test.backToProvider')}</Link>} />
      <section className="panel">
        <div className="pad">
          <dl className="definition" style={{ gridTemplateColumns: '1fr' }}>
            <div><dt>{t('test.outcome')}</dt><dd><StatusBadge status={r.outcome} /></dd></div>
            {r.failureReason ? <div><dt>{t('test.reason')}</dt><dd>{tk(`test.reason.${r.failureReason}`, {}, t('test.reason.other', { reason: r.failureReason }))}</dd></div> : null}
            {r.subject ? <div><dt>{t('test.subject')}</dt><dd><code data-ltr>{r.subject}</code></dd></div> : null}
            {r.email ? <div><dt>{t('test.email')}</dt><dd><span data-ltr>{r.email}</span> · {t('test.emailVerified')}: {r.emailVerified ? t('common.yes') : t('common.no')}</dd></div> : null}
            {r.groups.length ? <div><dt>{t('test.groups')}</dt><dd data-ltr>{r.groups.join(', ')}</dd></div> : null}
            <div><dt>{t('test.idpMfa')}</dt><dd>{r.idpMfa ? t('common.yes') : t('common.no')}</dd></div>
            {r.outcome === 'succeeded' ? <div><dt>{t('test.wouldResolve')}</dt><dd>{tk(`test.resolve.${r.wouldResolve}`)}</dd></div> : null}
            {r.matchedUser ? <div><dt>{t('test.matchedUser')}</dt><dd><Link to={`/users/${r.matchedUser.id}`} data-ltr>{r.matchedUser.email}</Link></dd></div> : null}
            {r.outcome === 'succeeded' ? <div><dt>{t('test.grants')}</dt><dd>{r.grantsFromMappings.length ? r.grantsFromMappings.map((g, i) => <div key={i}>{g.kind === 'specialist' ? tk(`specialist.${g.code}`) : tk(`role.${g.code}.title`, {}, g.code)} · {siteName(g.siteId)}</div>) : t('test.noGrants')}</dd></div> : null}
          </dl>
        </div>
      </section>
    </>
  );
}

// ------------------------------------------------------------------ user detail: sign-in identities
export function UserIdentities({ userId }: { userId: string }) {
  const { t, tk, dateTime } = useI18n();
  const { can, scopeKey } = useSession();
  const qc = useQueryClient();
  const key = [...scopeKey, 'identity', 'user', userId];
  const q = useQuery({ queryKey: key, queryFn: () => get<UserIdentitiesResponse>(`/api/v1/users/${userId}/identities`), enabled: can('identity.read') });
  const overview = useQuery({ queryKey: [...scopeKey, 'identity', 'overview'], queryFn: () => get<IdentityOverviewResponse>('/api/v1/identity/overview'), enabled: can('identity.link.manage') });
  const [adding, setAdding] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [providerId, setProviderId] = useState('');
  const [subject, setSubject] = useState('');
  if (!can('identity.read')) return null;
  const done = () => void qc.invalidateQueries({ queryKey: [...scopeKey, 'identity'] });
  return (
    <section className="panel" aria-labelledby="links-title">
      <div className="panel-head">
        <div><h2 id="links-title">{t('links.title')}</h2><p>{t('links.sub')}</p></div>
        {can('identity.link.manage') && overview.data?.providers.length ? <button className="button" type="button" onClick={() => { setProviderId(overview.data.providers[0]!.id); setAdding(true); }}>{t('links.add')}</button> : null}
      </div>
      <div className="pad">
        {q.isPending ? <Loading /> : q.isError ? <ErrorState error={q.error} /> : (
          <>
            <p>{t('links.source')}: {q.data.provisioning.source ? <>{tk(`links.source.${q.data.provisioning.source}`)}{q.data.provisioning.sourceName ? ` · ${q.data.provisioning.sourceName}` : ''}{q.data.provisioning.externalId ? <> · <code data-ltr>{q.data.provisioning.externalId}</code></> : null}</> : t('links.source.none')}</p>
            {q.data.links.length === 0 ? <p>{t('links.none')}</p> : (
              <ul className="stack">
                {q.data.links.map((l) => (
                  <li key={l.id} className="row">
                    <span><strong>{l.providerName}</strong> · <code data-ltr>{l.subject}</code> · {tk(`links.via.${l.linkedVia}`)} · {l.lastSignInAt ? dateTime(l.lastSignInAt) : t('identity.detail.never')}</span>
                    {can('identity.link.manage') ? <button className="button small danger" type="button" onClick={() => setRevoking(l.id)}>{t('links.revoke')}</button> : null}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      {adding ? (
        <ConfirmDialog title={t('links.add')} subtitle={t('links.addSub')} confirm={t('links.add')} run={(reason) => api('POST', `/api/v1/users/${userId}/identities`, { providerId, subject, reason })} onClose={() => setAdding(false)} onDone={done}>
          <Field label={t('links.provider')} full>{(p) => <select {...p} value={providerId} onChange={(e) => setProviderId(e.target.value)}>{overview.data?.providers.map((pr) => <option key={pr.id} value={pr.id}>{pr.displayName}</option>)}</select>}</Field>
          <Field label={t('links.subject')} full>{(p) => <input {...p} dir="ltr" value={subject} onChange={(e) => setSubject(e.target.value)} />}</Field>
        </ConfirmDialog>
      ) : null}
      {revoking ? <ConfirmDialog danger title={t('links.revoke')} subtitle={t('links.revokeSub')} confirm={t('links.revoke')} run={(reason) => api('POST', `/api/v1/users/${userId}/identities/${revoking}/revoke`, { reason })} onClose={() => setRevoking(null)} onDone={done} /> : null}
    </section>
  );
}

// ------------------------------------------------------------------ role preview (PB-17-01)
export function RolePreviewPage() {
  const { t, tk } = useI18n();
  const { scopeKey } = useSession();
  const q = useQuery({ queryKey: [...scopeKey, 'access-catalogue'], queryFn: () => get<AccessCatalogueResponse>('/api/v1/access/catalogue'), staleTime: Infinity });
  return (
    <>
      <PageHead eyebrow={t('users.eyebrow')} title={t('users.title')} intro={t('roles.sub')} />
      <UsersTabs />
      {q.isPending ? <Loading label={t('roles.loading')} /> : q.isError ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : (
        <section className="panel" aria-labelledby="roles-title">
          <div className="panel-head"><h2 id="roles-title">{t('roles.title')}</h2></div>
          <div className="tablewrap" tabIndex={0} aria-label={t('roles.tableLabel')} data-scroll-hint={t('common.scrollHint')}>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('roles.col.action')}</th>
                  <th scope="col">{t('roles.col.scope')}</th>
                  {q.data.experiences.map((e) => <th scope="col" key={e.id}>{tk(`role.${e.id}.title`)}</th>)}
                </tr>
              </thead>
              <tbody>
                {q.data.actions.map((a) => (
                  <tr key={a.id}>
                    <th scope="row"><code data-ltr>{a.id}</code>{a.sensitive ? <small>{t('roles.sensitive')}</small> : null}{a.entitlement ? <small>{t('roles.entitlement', { entitlement: a.entitlement })}</small> : null}</th>
                    <td>{t(a.scope === 'tenant' ? 'roles.scope.tenant' : 'roles.scope.site')}</td>
                    {q.data.experiences.map((e) => <td key={e.id}>{e.actions.includes(a.id) ? <span aria-label={t('roles.yes')}>●</span> : ''}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pad">
            <h3>{t('roles.specialists')}</h3>
            <ul className="stack">{q.data.specialistRoles.map((s) => <li key={s.id}><strong>{tk(`specialist.${s.id}`)}</strong> · {s.actions.map((x) => <code key={x} data-ltr>{x} </code>)}</li>)}</ul>
          </div>
        </section>
      )}
    </>
  );
}
